<template>
  <div class="ac-card-content">
    <div class="ac-header-container">
      <div class="info-content-container">
        <img
          class="header-img"
          src="@shared/assets/img/general/service-titan-full.png"
          alt="service-titan"
        />
        <div class="i-connected">
          <div class="green-dot" />
          <div class="label-info">Connected</div>
        </div>
      </div>
      <button type="button" class="ac-close-button" @click="toggle(false)">
        <img src="@shared/assets/img/general/x-black.png" alt="x" class="ac-close" />
      </button>
    </div>
    <div class="ac-body-container">
      <div class="integration-section">
        <div class="grid-column ai-s jc-b">
          <div>
            <div class="sac__font-20 semi-bold">
              Membership Plans Mapping
              <img
                v-if="loadingModal"
                src="@shared/assets/img/general/loading.svg"
                class="img-size-20"
                alt="loading"
              />
              <span v-else>
                ({{ membershipMappings.length }} / {{ Object.keys(membershipTypes).length }})</span
              >
            </div>
            <p class="sac__font-14 mt-2">
              Map your membership plans in ServiceTitan to SmartMaintenance plans within
              SmartAC.com.
            </p>
          </div>
          <button type="button" class="sac__button bg-transparent" @click="editMembershipMapping">
            <img
              src="@shared/assets/img/general/svg/edit-blue.svg"
              class="img-size-20 margin-right-4"
              alt="edit"
            />
            <span>Edit</span>
          </button>
        </div>
      </div>
      <div class="integration-section">
        <div class="grid-column ai-s jc-b">
          <div>
            <div class="sac__font-20 semi-bold grid-column ai-c jc-s gap-10">
              Job Types Mapping
              <img
                v-if="loadingModal"
                src="@shared/assets/img/general/loading.svg"
                class="img-size-20"
                alt="loading"
              />
              <div v-else class="grid-column ai-c jc-s gap-10">
                <span>({{ jobTypeMappings.length }} / {{ serviceConditions.length }})</span>
              </div>
            </div>
            <p class="sac__font-14 mt-2">
              Map your trade service offerings from SmartAC.com to your job types in ServiceTitan.
            </p>
          </div>
          <button
            type="button"
            class="sac__button bg-transparent"
            @click="toggle(true, 'titan-map-jobs', {})"
          >
            <img
              src="@shared/assets/img/general/svg/edit-blue.svg"
              class="img-size-20 margin-right-4"
              alt="edit"
            />
            <span>Edit</span>
          </button>
        </div>
      </div>
      <div class="integration-section">
        <div class="grid-column ai-s jc-b">
          <div>
            <div class="grid-column ai-c jc-s gap-10 sac__font-20 semi-bold">
              Tune-up Services Mapping
              <div :class="[tuneupMappings?.length > 0 ? 'green-dot' : 'yellow-dot']"></div>
            </div>
            <p class="sac__font-14 mt-2">
              Select and map the ServiceTitan Job Types that represent Tune-up to ensure proper
              tracking in SmartAC.com. This allows you to track all the Tune-up related services in
              SmartAC.com.
            </p>
          </div>
          <button
            type="button"
            class="sac__button bg-transparent"
            @click="toggle(true, 'titan-map-tuneup', {})"
          >
            <img
              src="@shared/assets/img/general/svg/edit-blue.svg"
              class="img-size-20 margin-right-4"
              alt="edit"
            />
            <span>Edit</span>
          </button>
        </div>
      </div>
      <AddCustomField />
      <AddTagType />
      <div class="integration-section">
        <div class="grid-column ai-s jc-b">
          <div>
            <div class="grid-column ai-c jc-s gap-10 sac__font-20 semi-bold">
              Comfort Credits Services Mapping
              <div :class="[invoiceMappings?.length > 0 ? 'green-dot' : 'yellow-dot']"></div>
            </div>
            <p class="sac__font-14 mt-2">
              Select and map the ServiceTitan pricebook services that represent Comfort Credits to
              ensure proper tracking in SmartAC.com. This allows you to designate which service
              entries in ServiceTitan should be recognized as Comfort Credits in SmartAC.com.
            </p>
          </div>
          <button
            type="button"
            class="sac__button bg-transparent"
            @click="toggle(true, 'titan-map-cc', {})"
          >
            <img
              src="@shared/assets/img/general/svg/edit-blue.svg"
              class="img-size-20 margin-right-4"
              alt="edit"
            />
            <span>Edit</span>
          </button>
        </div>
      </div>
      <ScheduleSetting />
      <div class="integration-section">
        <div class="grid-column ai-s gap-16">
          <div v-if="loadingSettings">
            <img src="@shared/assets/img/general/loading.svg" alt="loading" />
          </div>
          <SACToggleSwitch v-else :checked="enableEquipmentSync" :change="saveEquipmentSetting" />
          <div>
            <div class="sac__font-16 med">Add HVAC System Info to ServiceTitan</div>
            <div class="sac__font-14">
              Embed key HVAC system info into the location record in ServiceTitan so technicians can
              access a complete view of the home and equipment before arriving.
            </div>
          </div>
        </div>
        <div class="margin-top-32">
          <div class="sac__font-16 semi-bold">Need help?</div>
          <div class="sac__font-14 margin-top-4">
            If you have any questions or need assistance during the setup process, please feel free
            to contact your Partner Success Manager (PSM), who is ready to assist you.
          </div>
        </div>
      </div>
    </div>
    <div class="modal-footer-container">
      <button type="button" class="sac__button-med bg-red-transparent" @click="disconnect">
        Disconnect
      </button>
    </div>
    <ProcessOverlay :show="processing" text="Processing" overlayClass="ac-overlay" />
  </div>
</template>

<script>
import customerDetailMixin from '@shared/mixins/customerDetailMixin';
import { mapState } from 'vuex';
import SACInput from '@shared/components/sac/SACInput.vue';
import ProcessOverlay from '@shared/components/ProcessOverlay.vue';
import SACToggleSwitch from '@shared/components/sac/SACToggleSwitch.vue';
import SACTooltipItem from '@shared/components/sac/SACTooltip.vue';
import SACRadio from '@shared/components/sac/SACRadio.vue';
import AddCustomField from '@shared/components/setting/integrationsInfo/modals/components/AddCustomField.vue';
import AddTagType from '@shared/components/setting/integrationsInfo/modals/components/AddTagType.vue';
import ScheduleSetting from '@shared/components/setting/integrationsInfo/modals/components/ScheduleSetting.vue';

export default {
  name: 'IntegrationsEditTitanPopup',
  components: {
    ScheduleSetting,
    AddTagType,
    AddCustomField,
    SACRadio,
    SACTooltipItem,
    SACToggleSwitch,
    ProcessOverlay,
    SACInput,
  },
  mixins: [customerDetailMixin],
  props: {
    toggle: Function,
  },
  data() {
    return {
      processing: false,
      enableEquipmentSync: false,
      loadingModal: false,
    };
  },
  computed: {
    ...mapState({
      membershipMappings: (state) => state.serviceTitan.membershipMappings,
      membershipTypes: (state) => state.serviceTitan.membershipTypes || [],
      serviceConditions: (state) => state.serviceTitan.serviceConditions,
      jobTypeMappings: (state) => state.serviceTitan.jobTypeMappings,
      tuneupMappings: (state) => state.serviceTitan.tuneupMappings,
      invoiceMappings: (state) => state.serviceTitan.invoiceMappings,
      setting: (state) => state.setting.partnerSettings,
      partnerSetting: (state) => state.partnerDetail.partnerSettings,
      loadingSettings: (state) => state.setting.loadingSettings,
    }),
    partnerSettings() {
      if (this.isSmacAdmin) {
        return this.partnerSetting;
      }
      return this.setting;
    },
  },
  async mounted() {
    this.loadingModal = true;
    const allRequests = [];
    if (this.allCorpPlans.length <= 0) {
      allRequests.push(
        this.$store.dispatch('getPartnerProviderPlans', { corpId: this.customer.corporationId }),
      );
    }
    allRequests.push(this.$store.dispatch('getServiceConditionList'));
    if (Object.values(this.membershipTypes).length <= 0) {
      allRequests.push(this.$store.dispatch('getMembershipTypePlans'));
    }
    allRequests.push(this.$store.dispatch('getJobTypes'));
    allRequests.push(this.$store.dispatch('getStAvailabilitySetting'));
    allRequests.push(this.$store.dispatch('queryMappingsByCorpId'));

    await Promise.all(allRequests);
    this.setupInitValue();
    this.loadingModal = false;
  },
  methods: {
    editMembershipMapping() {
      this.$store.dispatch('changeServiceTitanValue', {
        field: 'backScreen',
        value: 'review',
      });
      this.toggle(true, 'titan-map-membership');
    },
    setupInitValue() {
      if (this.partnerSettings?.ST_EQUIPMENT_INTEGRATION) {
        this.enableEquipmentSync = this.partnerSettings.ST_EQUIPMENT_INTEGRATION === 'true';
      }
    },
    async saveEquipmentSetting() {
      this.enableEquipmentSync = !this.enableEquipmentSync;
      this.saveSetting('ST_EQUIPMENT_INTEGRATION', !!this.enableEquipmentSync);
    },
    saveSetting(name, value) {
      const payload = [
        {
          settingName: name,
          settingValue: value,
        },
      ];
      this.$store.dispatch('setPartnerSetting', payload);
    },
    async disconnect() {
      try {
        this.processing = true;
        await this.$store.dispatch('disconnectStIntegration');
        this.processing = false;
        this.toggle(false);
      } catch (err) {
        console.error(err);
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ac-card-content {
  .ac-header-container {
    display: flex;
    justify-content: space-between;
    .info-content-container {
      align-items: flex-start;
      display: inline-flex;
      gap: 8px;
      width: 100%;
      .header-img {
        width: 150px;
      }
      .i-connected {
        align-items: center;
        background-color: #66d38373;
        border-radius: 4px;
        display: inline-flex;
        gap: 8px;
        padding: 2px 8px;
        .label-info {
          color: $GREEN;
          font-weight: 600;
          font-size: 0.875rem;
        }
      }
    }
  }
  .integration-section {
    border-top: solid 1px $N30;
    margin: 1rem 0;
    padding-top: 25px;
    padding-bottom: 10px;
    &:first-child {
      border-top: unset;
      padding-top: 0;
    }
  }
}
</style>
